/**
 * Zustand store for authentication state including session, user,
 * team selection, and permissions. Persisted to localStorage.
 */
import type { Account, Session, User, TeamWithAccount } from "@nucleus/shared-types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/** Account info for selector (subset of full Account type) */
type AccountInfo = Pick<Account, "id" | "name" | "slug">;

interface AuthState {
	session: Session | null;
	user: User | null;
	teams: TeamWithAccount[];
	accounts: AccountInfo[];
	activeTeam: TeamWithAccount | null;
	permissions: string[];

	// Actions
	setSession: (session: Session | null) => void;
	setUser: (user: User | null) => void;
	setTeams: (teams: TeamWithAccount[]) => void;
	setAccounts: (accounts: AccountInfo[]) => void;
	setActiveTeam: (team: TeamWithAccount | null) => void;
	setPermissions: (permissions: string[]) => void;
	logout: () => void;

	// Deprecated aliases for backward compatibility
	/** @deprecated Use teams instead */
	brands: TeamWithAccount[];
	/** @deprecated Use activeTeam instead */
	activeBrand: TeamWithAccount | null;
	/** @deprecated Use setTeams instead */
	setBrands: (brands: TeamWithAccount[]) => void;
	/** @deprecated Use setActiveTeam instead */
	setActiveBrand: (brand: TeamWithAccount | null) => void;
}

/**
 * Authentication store
 *
 * Manages user session state with persistence.
 * Per user decision: sliding expiration handled by backend.
 */
export const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			session: null,
			user: null,
			teams: [],
			accounts: [],
			activeTeam: null,
			permissions: [],

			setSession: (session) => set({ session }),
			setUser: (user) => set({ user }),
			setTeams: (teams) => set({ teams }),
			setAccounts: (accounts) => set({ accounts }),
			setActiveTeam: (team) => set({ activeTeam: team }),
			setPermissions: (permissions) => set({ permissions }),

			logout: () =>
				set({
					session: null,
					user: null,
					teams: [],
					accounts: [],
					activeTeam: null,
					permissions: [],
				}),

			// Deprecated aliases - getters return the new state
			get brands() {
				return get().teams;
			},
			get activeBrand() {
				return get().activeTeam;
			},
			setBrands: (brands) => set({ teams: brands }),
			setActiveBrand: (brand) => set({ activeTeam: brand }),
		}),
		{
			name: "nucleus-auth",
			// Only persist certain fields
			partialize: (state) => ({
				activeTeam: state.activeTeam,
			}),
			// Migrate from old localStorage key format
			migrate: (persistedState) => {
				const state = persistedState as { activeBrand?: TeamWithAccount; activeTeam?: TeamWithAccount };
				// Migrate activeBrand -> activeTeam if old format exists
				if (state.activeBrand && !state.activeTeam) {
					return { activeTeam: state.activeBrand };
				}
				return state as { activeTeam: TeamWithAccount | null };
			},
			version: 1,
		}
	)
);
